/** Clients-list fetching with the same stale-while-revalidate cache + in-flight
 *  dedupe the jobs list uses (see useJobsList), minus the per-filter keying: the
 *  /clients/list endpoint takes no params and returns every client in one call
 *  (React does the search/filter/sort/pagination), so there's a single cache
 *  entry. Both the cache and the in-flight promise live at module scope so they
 *  survive ClientsList unmount/remount — which is what makes client → back →
 *  list instant (cached paint, background revalidate) instead of a cold refetch.
 *
 *  Dedupe: a concurrent second call shares the first's promise — this is what
 *  collapses React 18 StrictMode's dev double-invoke to one request.
 *
 *  reload(): invalidates the cache and revalidates (keeping the current rows on
 *  screen), for after a create so the new client shows without a spinner. */

import { useCallback, useEffect, useState } from "react";
import { clientsApi, ApiError } from "@/api";
import type { ClientsListResponse } from "@/types";

/** Skip a fresh revalidation only if we fetched within this window — just long
 *  enough to absorb a synchronous burst of mounts (StrictMode, a double bounce).
 *  Any real "leave and come back" is slower than this, so it revalidates. */
const DEDUP_WINDOW = 2_000;

interface CacheEntry {
  data: ClientsListResponse;
  fetchedAt: number;
}

let cache: CacheEntry | undefined;
let inflight: Promise<ClientsListResponse> | undefined;

/** Fetch with in-flight de-dup: a second caller before the first settles gets
 *  the same Promise. The settled result is cached; inflight clears on settle. */
function fetchDeduped(): Promise<ClientsListResponse> {
  if (inflight) return inflight;

  const p = clientsApi
    .listClients()
    .then((res) => {
      cache = { data: res, fetchedAt: Date.now() };
      return res;
    })
    .finally(() => {
      inflight = undefined;
    });

  inflight = p;
  return p;
}

function isWithinDedupWindow(): boolean {
  return !!cache && Date.now() - cache.fetchedAt < DEDUP_WINDOW;
}

export interface UseClientsListResult {
  rows: ClientsListResponse["items"];
  total: number;
  /** Cold load — no cached data to show yet. Drives the full-page spinner. */
  loading: boolean;
  /** Background revalidation while cached rows are on screen. Drives the subtle
   *  "Refreshing…" indicator so a return-to-list visibly updates. */
  refreshing: boolean;
  error?: string;
  /** Invalidate + revalidate (e.g. after creating a client). */
  reload: () => void;
}

interface State {
  data?: ClientsListResponse;
  loading: boolean;
  refreshing: boolean;
  error?: string;
}

export function useClientsList(): UseClientsListResult {
  // Seed from cache on first render so the first paint after a back-nav is
  // instant (no loading flash) when we already hold the data.
  const [state, setState] = useState<State>(() =>
    cache
      ? { data: cache.data, loading: false, refreshing: !isWithinDedupWindow() }
      : { loading: true, refreshing: false },
  );

  // Bump to force the effect to re-run (manual reload after a create).
  const [reloadTick, setReloadTick] = useState(0);
  const reload = useCallback(() => {
    cache = undefined; // invalidate so the effect revalidates rather than no-ops
    setReloadTick((t) => t + 1);
  }, []);

  useEffect(() => {
    let cancelled = false;

    if (cache) {
      if (isWithinDedupWindow()) {
        // Just fetched (synchronous mount burst) — show it, don't refetch.
        setState({ data: cache.data, loading: false, refreshing: false });
        return;
      }
      // Returning to the list → paint cached now, revalidate in the background.
      setState({ data: cache.data, loading: false, refreshing: true });
    } else {
      // Cold load shows the spinner; a manual reload keeps prior rows visible
      // (refreshing) rather than flashing the spinner.
      setState((s) =>
        s.data
          ? { data: s.data, loading: false, refreshing: true }
          : { loading: true, refreshing: false },
      );
    }

    fetchDeduped()
      .then((res) => {
        if (!cancelled) setState({ data: res, loading: false, refreshing: false });
      })
      .catch((err) => {
        if (cancelled) return;
        const message = err instanceof ApiError ? err.message : "Could not load clients.";
        // Keep any cached rows on screen if a background revalidate fails.
        setState((s) => ({ data: s.data, loading: false, refreshing: false, error: message }));
      });

    // Cleanup deliberately does NOT clear the inflight entry — that's what lets
    // StrictMode's second effect run reuse the first run's request.
    return () => {
      cancelled = true;
    };
  }, [reloadTick]);

  return {
    rows: state.data?.items ?? [],
    total: state.data?.total ?? 0,
    loading: state.loading,
    refreshing: state.refreshing,
    error: state.error,
    reload,
  };
}
